import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  CheckCircle2,
  Package,
  MapPin,
  CreditCard,
  Gift,
  Printer,
  ArrowLeft,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  Truck,
  ExternalLink,
  Clock,
  Navigation,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import API from '../config/api';
import { toast } from 'sonner';

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Live tracking modal state
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [liveTracking, setLiveTracking] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  const isSuccessRedirect = searchParams.get('success') === 'true';

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setOrder(res.data);
      setError(null);
    } catch (err) {
      console.error('Fetch order error:', err);
      setError(err.response?.data?.detail || 'Order not found or access denied');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handlePrint = () => {
    window.print();
  };

  const handleOpenTracking = async () => {
    setTrackingModalOpen(true);
    setLoadingTracking(true);
    try {
      const res = await axios.get(`${API}/shipping/track/${orderId}`);
      setLiveTracking(res.data);
    } catch (err) {
      console.error('Failed to fetch live tracking:', err);
      toast.error('Unable to fetch live tracking events.');
    } finally {
      setLoadingTracking(false);
    }
  };

  const getTimelineStep = (status, paymentStatus, shiprocketStatus) => {
    if (status === 'cancelled') return -1;
    if (status === 'delivered' || shiprocketStatus === 'DELIVERED') return 4;
    if (status === 'shipped' || shiprocketStatus === 'IN_TRANSIT' || shiprocketStatus === 'AWB_ASSIGNED' || shiprocketStatus === 'PICKUP_SCHEDULED') return 3;
    if (status === 'processing' || status === 'paid' || shiprocketStatus === 'PROCESSING') return 2;
    if (paymentStatus === 'completed' || paymentStatus === 'cod' || status === 'confirmed') return 1;
    return 0;
  };

  const timelineSteps = [
    { label: 'Order Placed', desc: 'Order received' },
    { label: 'Payment Confirmed', desc: 'Authorized' },
    { label: 'Sacred Packaging', desc: 'Blessed & packed' },
    { label: 'Shiprocket Dispatched', desc: 'In courier transit' },
    { label: 'Delivered', desc: 'Consecrated arrival' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-10 h-10 border-3 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-[#83725B] text-xs font-semibold">Loading sacred order details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex flex-col text-[#2A1F16]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-20 pb-12 px-4">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm w-full border border-[#E7D9BC] shadow-xs">
            <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
              <Package className="w-6 h-6" />
            </div>
            <h2 className="font-serif-heading text-xl font-bold text-[#4E1225] mb-1.5">Order Not Found</h2>
            <p className="text-[#83725B] text-xs mb-5">{error || 'Unable to retrieve order details.'}</p>
            <div className="flex flex-col gap-2.5">
              <Link to="/dashboard">
                <Button className="w-full rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white text-xs font-bold py-2.5">
                  Go to My Orders
                </Button>
              </Link>
              <Link to="/store">
                <Button variant="outline" className="w-full rounded-md border-[#E7D9BC] text-xs font-semibold py-2.5">
                  Explore Store
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const currentStep = getTimelineStep(order.status, order.payment_status, order.shiprocket_status);

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex flex-col text-[#2A1F16] print:bg-white print:pt-0">
      <div className="print:hidden">
        <Navbar />
      </div>

      <main className="flex-1 pt-20 sm:pt-24 pb-12 print:pt-2 print:pb-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Back & Print */}
          <div className="flex items-center justify-between mb-4 print:hidden">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#83725B] hover:text-[#4E1225] bg-white/80 px-3 py-1.5 rounded-full border border-[#E7D9BC] shadow-2xs transition-all hover:-translate-x-0.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Dashboard</span>
            </button>

            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4E1225] bg-white px-3 py-1.5 rounded-full border border-[#E7D9BC] shadow-2xs hover:border-[#6E1B34] transition-all"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print Invoice</span>
            </button>
          </div>

          {/* Success Banner */}
          {isSuccessRedirect && (
            <motion.div
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-[#2E7D45] to-[#1B5E20] rounded-2xl p-4 sm:p-5 text-white shadow-md mb-6 relative overflow-hidden print:hidden"
            >
              <div className="flex items-start sm:items-center gap-3 relative z-10">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-serif-heading text-lg sm:text-xl font-bold mb-0.5">
                    Order Placed Successfully!
                  </h1>
                  <p className="text-white/90 text-xs">
                    An email invoice with complete payment details has been dispatched to <strong>{order.shipping_address?.email || user?.email}</strong>.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Main Card */}
          <div className="bg-white rounded-2xl border border-[#E7D9BC] shadow-xs overflow-hidden">
            
            {/* Header / Invoice Meta */}
            <div className="p-4 sm:p-6 border-b border-[#E7D9BC]/60 bg-[#FBF4E7]/40">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="px-2.5 py-0.5 bg-[#FBF4E7] text-[#6E1B34] border border-[#E7D9BC] rounded-full text-[10px] font-bold uppercase tracking-wider">
                      Order Details
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      order.payment_status === 'completed' || order.status === 'paid'
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : order.payment_status === 'cod'
                        ? 'bg-blue-50 text-blue-800 border border-blue-200'
                        : 'bg-amber-50 text-amber-800 border border-amber-200'
                    }`}>
                      {order.payment_status === 'completed' || order.status === 'paid' ? 'Paid Online' : order.payment_status === 'cod' ? 'Cash on Delivery' : 'Payment Pending'}
                    </span>
                  </div>
                  <h2 className="font-serif-heading text-xl sm:text-2xl font-bold text-[#4E1225]">
                    Order #{order.id}
                  </h2>
                  <p className="text-xs text-[#83725B] mt-0.5">
                    Placed on {new Date(order.created_at || Date.now()).toLocaleDateString('en-IN', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                <div className="text-left sm:text-right">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#83725B] block">
                    Total Amount Paid
                  </span>
                  <span className="font-price text-2xl sm:text-3xl font-bold text-[#6E1B34]">
                    ₹{(order.total_amount || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Status Progress */}
            <div className="p-4 sm:p-6 border-b border-[#E7D9BC]/60 bg-[#FAF7F0]/40">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#83725B] mb-4">
                Fulfillment Status
              </h3>
              
              <div className="relative">
                <div className="hidden sm:block absolute top-3 left-6 right-6 h-0.5 bg-[#E7D9BC] -z-0">
                  <div
                    className="h-full bg-[#6E1B34] transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, (currentStep / (timelineSteps.length - 1)) * 100))}%` }}
                  ></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 relative z-10">
                  {timelineSteps.map((step, idx) => {
                    const isDone = idx <= currentStep;
                    return (
                      <div key={step.label} className="flex sm:flex-col items-center sm:text-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          isDone
                            ? 'bg-[#6E1B34] text-white'
                            : 'bg-white text-gray-400 border border-[#E7D9BC]'
                        }`}>
                          {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx + 1}
                        </div>
                        <div>
                          <p className={`text-xs font-bold ${isDone ? 'text-[#4E1225]' : 'text-gray-400'}`}>
                            {step.label}
                          </p>
                          <p className="text-[10px] text-[#83725B] hidden sm:block">
                            {step.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Shiprocket Logistics Delivery Information Card */}
            <div className="m-4 sm:m-6 p-4 bg-white border border-[#E7D9BC] rounded-xl shadow-2xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FBF4E7] border border-[#E7D9BC] flex items-center justify-center flex-shrink-0 text-[#6E1B34]">
                    <Truck className="w-5 h-5 text-[#D9711F]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-serif-heading font-bold text-sm sm:text-base text-[#4E1225]">
                        {order.shipping_package?.name || 'Standard Sacred Delivery'}
                      </h4>
                      <span className="px-2 py-0.5 bg-[#FBF4E7] text-[#6E1B34] border border-[#E7D9BC] rounded-full text-[10px] font-bold">
                        {order.shipping_package?.badge || 'Shiprocket Delivery'}
                      </span>
                    </div>

                    <p className="text-xs text-[#83725B] mt-0.5">
                      Carrier: <strong className="text-[#2A1F16]">{order.courier_name || order.shipping_package?.courier_name || 'Delhivery Express'}</strong>
                      {order.shipping_package?.etd && <span> • Estimated Transit: <strong>{order.shipping_package.etd}</strong></span>}
                    </p>

                    {order.awb_code && (
                      <p className="text-xs text-[#4E1225] font-mono mt-1 font-bold">
                        AWB Tracking: <span className="bg-[#FAF7F0] px-1.5 py-0.5 rounded border border-[#E7D9BC]">{order.awb_code}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={handleOpenTracking}
                    size="sm"
                    className="bg-[#6E1B34] hover:bg-[#4E1225] text-white text-xs font-bold rounded-lg px-3.5 py-1.5 shadow-2xs"
                  >
                    <Navigation className="w-3.5 h-3.5 mr-1" />
                    Live Tracking
                  </Button>

                  {order.tracking_url && (
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#83725B] hover:text-[#4E1225] bg-[#FAF7F0] px-3 py-1.5 rounded-lg border border-[#E7D9BC]"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Shiprocket Portal</span>
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Gift Package Banner */}
            {order.is_gift && (
              <div className="m-4 sm:m-6 mt-0 p-4 bg-[#FBF4E7] border border-[#E7D9BC] rounded-xl text-xs">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white shadow-2xs border border-[#E7D9BC] flex items-center justify-center flex-shrink-0 text-[#6E1B34]">
                    <Gift className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-bold text-[#4E1225] text-sm">Sacred Gift Package</span>
                      {order.gift_wrap && (
                        <span className="px-2 py-0.5 bg-[#F3E7D0] text-[#B85A15] text-[10px] font-bold rounded border border-[#E7D9BC]">
                          ✨ Himalayan Sacred Wrap
                        </span>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-1.5 text-xs text-[#2A1F16] mt-1">
                      {order.gift_sender_name && <div><strong>From:</strong> {order.gift_sender_name}</div>}
                      {order.gift_recipient_name && <div><strong>To:</strong> {order.gift_recipient_name}</div>}
                    </div>

                    {order.gift_message && (
                      <div className="mt-2 p-2 bg-white/80 rounded-lg border border-[#E7D9BC]/60 text-[#4E1225] italic text-[11px]">
                        "{order.gift_message}"
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Items Ordered */}
            <div className="p-4 sm:p-6 border-b border-[#E7D9BC]/60">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#83725B] mb-3">
                Ordered Items ({order.items?.length || 0})
              </h3>

              <div className="divide-y divide-[#E7D9BC]/40">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden bg-[#FAF7F0] flex-shrink-0 border border-[#E7D9BC]">
                      <img
                        src={item.image_url || 'https://images.unsplash.com/photo-1692107089367-791b734a426c?w=100'}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif-heading font-bold text-[#4E1225] text-sm truncate">
                        {item.name}
                      </h4>
                      {item.origin && (
                        <p className="text-[10px] text-[#83725B] flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-[#D9711F]"></span>
                          <span>{item.origin}</span>
                        </p>
                      )}
                      <p className="text-xs text-[#83725B]">
                        Qty: <strong className="text-[#2A1F16]">{item.quantity}</strong> &times; ₹{(item.price || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-[#4E1225] text-sm sm:text-base">
                        ₹{((item.price || 0) * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Address & Payment Grid */}
            <div className="p-4 sm:p-6 grid md:grid-cols-2 gap-4 bg-[#FAF7F0]/40">
              
              <div className="space-y-3">
                {/* Delivery Address */}
                <div className="bg-white p-4 rounded-xl border border-[#E7D9BC]">
                  <div className="flex items-center gap-1.5 mb-2 text-[#4E1225] font-bold text-xs">
                    <MapPin className="w-3.5 h-3.5 text-[#D9711F]" />
                    <span>Delivery Address</span>
                  </div>
                  {order.shipping_address ? (
                    <div className="text-xs text-[#83725B] space-y-0.5">
                      <p className="font-bold text-[#2A1F16]">{order.shipping_address.name}</p>
                      <p>{order.shipping_address.address}</p>
                      <p>{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
                      <p className="text-[11px] pt-1">Phone: {order.shipping_address.phone}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">Address not available</p>
                  )}
                </div>

                {/* Payment Info */}
                <div className="bg-white p-4 rounded-xl border border-[#E7D9BC] text-xs">
                  <div className="flex items-center gap-1.5 mb-2 text-[#4E1225] font-bold">
                    <CreditCard className="w-3.5 h-3.5 text-[#D9711F]" />
                    <span>Payment Details</span>
                  </div>
                  <div className="space-y-1 text-[#83725B]">
                    <div className="flex justify-between">
                      <span>Method:</span>
                      <span className="font-semibold text-[#2A1F16] capitalize">
                        {order.payment_method === 'online' ? 'Razorpay (Online)' : order.payment_method}
                      </span>
                    </div>
                    {order.razorpay_payment_id && (
                      <div className="flex justify-between">
                        <span>Payment ID:</span>
                        <span className="font-mono font-bold text-[#6E1B34]">{order.razorpay_payment_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-white p-4 rounded-xl border border-[#E7D9BC] flex flex-col justify-between text-xs">
                <div>
                  <h4 className="font-serif-heading font-bold text-[#4E1225] mb-3 pb-1.5 border-b border-[#E7D9BC]/60">
                    Payment Breakdown
                  </h4>

                  <div className="space-y-1.5 text-[#83725B]">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span className="font-bold text-[#2A1F16]">₹{(order.subtotal || order.total_amount).toLocaleString()}</span>
                    </div>

                    {order.discount > 0 && (
                      <div className="flex justify-between text-emerald-700 font-semibold">
                        <span>Coupon Discount {order.coupon_code ? `(${order.coupon_code})` : ''}</span>
                        <span>-₹{order.discount.toLocaleString()}</span>
                      </div>
                    )}

                    {order.gift_wrap_charge > 0 && (
                      <div className="flex justify-between">
                        <span>Sacred Gift Packaging</span>
                        <span className="font-bold text-[#2A1F16]">₹{order.gift_wrap_charge.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span>
                        Delivery Fee ({order.shipping_package?.name || 'Package'})
                      </span>
                      <span className={order.shipping_cost === 0 || !order.shipping_cost ? 'text-emerald-700 font-bold' : 'font-bold text-[#2A1F16]'}>
                        {order.shipping_cost === 0 || !order.shipping_cost ? 'FREE' : `₹${order.shipping_cost}`}
                      </span>
                    </div>

                    <div className="h-px bg-[#E7D9BC]/60 my-1.5"></div>

                    <div className="flex justify-between items-baseline pt-0.5">
                      <div>
                        <span className="font-bold text-[#4E1225] text-sm">Grand Total</span>
                        <p className="text-[10px] text-[#83725B]">All taxes included</p>
                      </div>
                      <span className="font-price font-bold text-xl text-[#6E1B34]">
                        ₹{(order.total_amount || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-2 border-t border-[#E7D9BC]/40">
                  <div className="flex items-center gap-1.5 text-[10px] text-[#83725B]">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#2E7D45]" />
                    <span>Protected by 256-bit encrypted Razorpay checkout</span>
                  </div>
                </div>
              </div>

            </div>

            {/* Bottom Actions */}
            <div className="p-4 sm:p-5 bg-[#FBF4E7] border-t border-[#E7D9BC] flex flex-col sm:flex-row items-center justify-between gap-3 print:hidden">
              <a
                href="https://wa.me/917011134934?text=Namaste,%20I%20have%20an%20inquiry%20regarding%20my%20order"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#2E7D45] hover:text-[#1B5E20] text-xs font-bold bg-white px-3 py-1.5 rounded-full border border-[#C8E6C9] shadow-2xs"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>WhatsApp Support (+91 7011134934)</span>
              </a>

              <Link to="/store">
                <Button className="rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white text-xs font-bold px-6 py-2">
                  Continue Shopping
                </Button>
              </Link>
            </div>

          </div>

        </div>
      </main>

      {/* Live Tracking Modal */}
      <AnimatePresence>
        {trackingModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 sm:p-6 border border-[#E7D9BC] shadow-xl"
            >
              <div className="flex items-center justify-between pb-3 border-b border-[#E7D9BC]">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#6E1B34]" />
                  <h3 className="font-serif-heading text-lg font-bold text-[#4E1225]">
                    Shiprocket Live Tracking
                  </h3>
                </div>
                <button
                  onClick={() => setTrackingModalOpen(false)}
                  className="w-8 h-8 rounded-full bg-[#FAF7F0] text-gray-500 hover:text-black flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {loadingTracking ? (
                <div className="py-12 text-center">
                  <div className="w-8 h-8 border-2 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-xs text-[#83725B]">Connecting to Shiprocket courier network...</p>
                </div>
              ) : (
                <div className="mt-4 space-y-4 text-xs">
                  {/* Status Banner */}
                  <div className="p-3 bg-[#FBF4E7] rounded-xl border border-[#E7D9BC] flex items-center justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-[#83725B]">Current Status</span>
                      <p className="font-bold text-[#4E1225] text-sm capitalize">
                        {liveTracking?.tracking?.current_status || order.status || 'In Transit'}
                      </p>
                    </div>
                    <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-bold rounded-full text-[10px]">
                      {liveTracking?.courier_name || order.courier_name || 'Courier Partner'}
                    </span>
                  </div>

                  {order.awb_code && (
                    <div className="flex justify-between items-center text-xs px-1">
                      <span className="text-[#83725B]">AWB Number:</span>
                      <span className="font-mono font-bold text-[#4E1225]">{order.awb_code}</span>
                    </div>
                  )}

                  {/* Scans Timeline */}
                  <div className="space-y-3 pt-2">
                    <h5 className="font-bold text-[#4E1225] uppercase tracking-wider text-[10px]">
                      Tracking Milestones
                    </h5>
                    
                    <div className="space-y-3 border-l-2 border-[#E7D9BC] ml-2 pl-4">
                      {liveTracking?.tracking?.scans && liveTracking.tracking.scans.length > 0 ? (
                        liveTracking.tracking.scans.map((scan, idx) => (
                          <div key={idx} className="relative">
                            <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-[#6E1B34]"></div>
                            <p className="font-bold text-[#4E1225]">{scan.activity}</p>
                            <p className="text-[10px] text-[#83725B]">{scan.location} • {new Date(scan.date).toLocaleString('en-IN')}</p>
                          </div>
                        ))
                      ) : (
                        <div className="relative">
                          <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-[#6E1B34]"></div>
                          <p className="font-bold text-[#4E1225]">Order Packed & Handed to Shiprocket Partner</p>
                          <p className="text-[10px] text-[#83725B]">Haridwar Central Shrine Hub, Uttarakhand</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-[#E7D9BC] flex justify-end">
                    <Button
                      onClick={() => setTrackingModalOpen(false)}
                      className="bg-[#6E1B34] hover:bg-[#4E1225] text-white text-xs font-bold"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
};

export default OrderDetailsPage;
